import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Wallet,
  LayoutDashboard,
  Target,
  FolderTree,
  Upload,
  Zap,
  LogOut,
  User,
  Menu,
  X,
  PiggyBank,
  Settings,
  TrendingUp,
  CircleDollarSign,
  GitCompare,
} from "lucide-react";
import { toast } from "sonner";
import DebugInfo from "@/components/DebugInfo";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/goals", icon: Target, label: "Budget Goals" },
  { path: "/long-term", icon: TrendingUp, label: "Long-Term" },
  { path: "/retirement", icon: PiggyBank, label: "Retirement" },
  { path: "/other-savings", icon: CircleDollarSign, label: "Other Savings" },
  { path: "/categories", icon: FolderTree, label: "Categories" },
  { path: "/import", icon: Upload, label: "Import Data" },
  { path: "/reconcile", icon: GitCompare, label: "Reconcile" },
  { path: "/quick-check", icon: Zap, label: "Quick Check" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [passwordHint, setPasswordHint] = useState("");
  const [savingHint, setSavingHint] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleOpenSettings = () => {
    setPasswordHint("");
    setSettingsOpen(true);
  };

  const handleSaveHint = async () => {
    if (!passwordHint.trim()) {
      toast.error("Please enter a password hint");
      return;
    }
    
    setSavingHint(true);
    try {
      await api.put("/auth/hint", { password_hint: passwordHint });
      toast.success("Password hint updated successfully");
      setSettingsOpen(false);
      setPasswordHint("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update hint");
    } finally {
      setSavingHint(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-900 rounded-lg">
                <Wallet className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-bold font-['Outfit'] tracking-tight text-slate-900">
                MEF Companion
              </span>
            </div>

            {/* Hamburger Menu for All Screen Sizes */}
            <div className="flex items-center gap-2">
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    data-testid="nav-menu-toggle"
                  >
                    {menuOpen ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {navItems.map((item) => (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMenuOpen(false);
                      }}
                      className="cursor-pointer"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <item.icon className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2"
                    data-testid="user-menu-trigger"
                  >
                    <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="h-4 w-4 text-slate-600" strokeWidth={1.5} />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium text-slate-700">
                      {user?.display_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-slate-900">{user?.display_name}</p>
                    <p className="text-xs text-slate-500">@{user?.username}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleOpenSettings}
                    className="cursor-pointer"
                    data-testid="settings-btn"
                  >
                    <Settings className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
            <DialogDescription>
              Update your account settings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="password-hint">Password Hint</Label>
              <Input
                id="password-hint"
                placeholder="Enter a hint to help you remember your password"
                value={passwordHint}
                onChange={(e) => setPasswordHint(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                This hint will be shown on the login page if you forget your password
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveHint} disabled={savingHint}>
              {savingHint ? "Saving..." : "Save Hint"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Debug Info Bar */}
      <DebugInfo />
    </div>
  );
}
